import React from 'react';

export type SimulationStatus = 'ACTIVE' | 'DRAFT' | 'CLOSED';

export type SimulationRow = {
  simulationCode: string;
  role: string;
  variants: number | string;
  reportsReady: number | string;
  status: SimulationStatus;
  actionLabel: 'View Reports' | 'Edit';
  onAction: () => void;
};

type SimulationsTableProps = {
  rows: SimulationRow[];
};

const statusBadgeClass: Record<SimulationStatus, string> = {
  ACTIVE: 'bg-[#f0fdfa] text-[#0d9488]',
  DRAFT: 'bg-[#f5f5f4] text-[#78716c]',
  CLOSED: 'bg-[#fef2f2] text-[#dc2626]',
};

const SimulationsTable: React.FC<SimulationsTableProps> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <div className="rounded-b-[14px] border border-t-0 border-[#e2e5eb] bg-white px-6 py-10 text-center">
        <p className="font-display text-[20px] font-bold text-[#0f172a]">No simulations available yet</p>
        <p className="mt-2 text-[13px] text-[#64748b]">
          Create or activate a simulation variant to start generating candidate links and reports.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-b-[14px] border border-t-0 border-[#e2e5eb] bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-[760px] w-full text-left">
          <thead>
            <tr className="bg-[#fafdf9]">
              {['Role', 'Variants', 'Reports Ready', 'Status', 'Action'].map((header) => (
                <th
                  key={header}
                  className="border-b-2 border-[#0d9488] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#475569]"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.simulationCode}
                onClick={row.onAction}
                className="cursor-pointer border-b border-[#f0f0ee] transition-colors last:border-b-0 hover:bg-[#fafdf9]"
              >
                <td className="px-4 py-3 text-[14px] font-semibold text-[#0f172a]">{row.role}</td>
                <td className="px-4 py-3 font-mono text-[13px] text-[#475569]">{row.variants}</td>
                <td className="px-4 py-3 font-mono text-[13px] text-[#475569]">{row.reportsReady}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusBadgeClass[row.status]}`}>
                    {row.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      row.onAction();
                    }}
                    className="rounded-[10px] border border-[#e2e5eb] bg-white px-3.5 py-2 text-[12px] font-semibold text-[#475569] transition-colors hover:border-[#0d9488] hover:text-[#0d9488]"
                  >
                    {row.actionLabel}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SimulationsTable;
